import React from 'react';
import * as Yup from 'yup';

import { Form } from '@dashboard/components/Formik';
import usePage from '@dashboard/hooks/usePage';
import { format } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import timezones from '@dashboard/services/timezones';
import getGmtOffsetForTimezone from '@dashboard/services/getGmtOffsetForTimezone';

Yup.setLocale({
  mixed: {
    required: ({ path }) => `${path} is required`,
  },
});

function parseDate(date, timezone) {
  return timezone ? toZonedTime(date, timezone) : new Date(date);
}

const EventDetailsForm = () => {
  const { page, updatePage } = usePage();

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <Form
      initialValues={{
        showAddMapsLink: !!page.eventDetails.mapsLink,
        title: page.title,
        eventDetails: {
          startDate: page.eventDetails.startDate
            ? format(
                parseDate(page.eventDetails.startDate, page.eventDetails.timezone),
                'yyyy-MM-dd HH:mm',
              )
            : undefined,
          endDate: page.eventDetails.endDate
            ? format(
                parseDate(page.eventDetails.endDate, page.eventDetails.timezone),
                'yyyy-MM-dd HH:mm',
              )
            : undefined,
          timezone: page.eventDetails.timezone || userTimezone,
          type: page.eventDetails.type,
          location: page.eventDetails.location,
          mapsLink: page.eventDetails.mapsLink,
          capacity: page.eventDetails.capacity === null ? null : page.eventDetails.capacity,
          currency: page.eventDetails.currency,
        },
      }}
      onSubmit={async (values, actions) => {
        const eventDetails = {
          ...page.eventDetails,
          ...values.eventDetails,
          timezone: values.eventDetails.timezone,
        };

        if (values.eventDetails.startDate) {
          eventDetails.startDate = fromZonedTime(
            values.eventDetails.startDate,
            values.eventDetails.timezone,
          );
        } else {
          eventDetails.startDate = undefined;
        }

        if (values.eventDetails.endDate) {
          eventDetails.endDate = fromZonedTime(
            values.eventDetails.endDate,
            values.eventDetails.timezone,
          );
        } else {
          eventDetails.endDate = undefined;
        }

        const { data, error } = await updatePage.mutateAsync({
          title: values.title,
          eventDetails: eventDetails,
          type: 'event',
        });

        actions.resetForm({
          values: {
            showAddMapsLink: !!data.eventDetails.mapsLink,
            title: data.title,
            eventDetails: {
              ...page.eventDetails,
              ...data.eventDetails,
              startDate: data.eventDetails.startDate
                ? format(
                    toZonedTime(data.eventDetails.startDate, data.eventDetails.timezone),
                    'yyyy-MM-dd HH:mm',
                  )
                : undefined,
              endDate: data.eventDetails.endDate
                ? format(
                    toZonedTime(data.eventDetails.endDate, data.eventDetails.timezone),
                    'yyyy-MM-dd HH:mm',
                  )
                : undefined,
            },
          },
        });

        return {
          data: data,
          error: error,
        };
      }}
      fields={({ values, setFieldValue }) => {
        return [
          {
            name: 'title',
            label: 'Event name',
            type: 'text',
            placeholder: 'Event name',
            autoFocus: true,
          },
          {
            type: 'columns',
            columns: 2,
            fields: [
              {
                name: 'eventDetails.startDate',
                label: 'Start date',
                type: 'datetime-local',
                required: !!values.eventDetails.endDate,
              },
              {
                name: 'eventDetails.endDate',
                label: 'End date',
                type: 'datetime-local',
              },
            ],
          },
          {
            type: 'columns',
            columns: 2,
            fields: [
              {
                name: 'eventDetails.type',
                label: 'Attendance',
                type: 'select',
                options: [
                  {
                    value: '',
                    label: 'Select event type',
                    disabled: true,
                  },
                  { value: 'in_person', label: 'In person' },
                  { value: 'virtual', label: 'Virtual' },
                ],
                required: true,
              },
              {
                name: 'eventDetails.timezone',
                required: false,
                type: 'select',
                label: 'Timezone',
                tooltip:
                  'The timezone used for the event, on the event page and in emails to attendees',
                options: timezones.map(tz => ({
                  value: tz,
                  label: `${tz} (GMT ${getGmtOffsetForTimezone(tz)})`
                    .replace(/_/g, ' ')
                    .replace('/', ' - ')
                    .replace('Etc - UTC', 'UTC'),
                })),
              },
            ],
            reduceMargin: values.eventDetails.type === 'in_person',
          },
          {
            name: 'eventDetails.location',
            label: 'Location',
            type: 'text',
            visible: values.eventDetails.type === 'in_person',
            reduceMargin: true,
          },
          {
            name: 'showAddMapsLink',
            type: 'custom',
            renderField: () => (
              <button
                type="button"
                onClick={() => setFieldValue('showAddMapsLink', true)}
                className="text-sm font-medium underline"
              >
                + Add maps link
              </button>
            ),
            visible: values.eventDetails.type === 'in_person' && !values.showAddMapsLink,
          },
          {
            name: 'eventDetails.mapsLink',
            label: 'Maps link',
            type: 'url',
            visible: values.eventDetails.type === 'in_person' && values.showAddMapsLink === true,
          },
          {
            type: 'columns',
            columns: 3,
            fields: [
              {
                name: 'eventDetails.capacity',
                label: 'Max. capacity',
                type: 'number',
              },
            ],
          },
          {
            type: 'columns',
            columns: 3,
            fields: [
              {
                name: 'eventDetails.currency',
                label: 'Currency',
                type: 'currency',
              },
            ],
          },
        ];
      }}
    />
  );
};

export default EventDetailsForm;
